<template>
  <div id="vpsDialog">
    <vDialog :visible.sync="visible" :visibleFooter="false" :needFullScreen="true" :width="isMobile ? '' : '520px'">
      <div slot="header" class="dialog_header">{{ $t('promotion.vps.submitVpsInv') }}</div>
      <div class="vps_content">
        <div class="body_invoice_wrapper mb-8">
          <p>{{ $t('promotion.vps.uploadInvoice') }}</p>
          <div class="mt-4">
            <ul>
              <li>
                {{ $t('promotion.vps.name') }}
                <el-tooltip
                  :content="$t('promotion.vps.nameComplement')"
                  placement="bottom-start"
                  popper-class="vps_tooltip"
                >
                  <svg-icon iconName="info-circle-icon" class="info_icon"></svg-icon>
                </el-tooltip>
              </li>
              <li>{{ $t('promotion.vps.amount') }}</li>
              <li>{{ $t('promotion.vps.email') }}</li>
              <li>{{ $t('promotion.vps.billPeriod') }}</li>
              <li>{{ $t('promotion.vps.currency') }}</li>
            </ul>
          </div>
        </div>
        <div class="upload_wrapper">
          <el-form label-position="top" :model="form" ref="form" :rules="rules">
            <el-row :gutter="40">
              <el-col :xs="36" :sm="24">
                <div class="upload_header mb-2">{{ $t('common.field.upload') }}</div>
                <el-form-item prop="uploadFile" class="upload_form_item">
                  <div>
                    <p>
                      {{ $t('promotion.vps.fileLimit') }},{{ $t('promotion.vps.fileSize') }},{{
                        $t('promotion.vps.fileNumber')
                      }}
                    </p>
                  </div>
                  <vUpload
                    :limit="1"
                    :allowedFile="allowedFiles"
                    v-on:updateFileInfo="updateFileInfo"
                    :accept="acceptableFiles"
                    data-testid="updateFileInfo"
                    ref="upload"
                    :selfTip="true"
                    :selfText="true"
                  >
                    <div v-show="!showInfoField">
                      <div class="select_file_btn mt-4" data-testid="select_file_btn">
                        <i class="el-icon-plus"></i> {{ $t('promotion.vps.selectFile') }}
                      </div>
                    </div>
                  </vUpload>
                </el-form-item>
              </el-col>
            </el-row>
            <div class="form_container" v-if="showInfoField">
              <el-row :gutter="40">
                <el-col :xs="24" :sm="24">
                  <el-form-item :label="$t('promotion.vps.refundAccount')" prop="refundAccount">
                    <el-select
                      v-model="form.refundAccount"
                      :placeholder="$t('common.column.tradingAcc')"
                      data-testid="refundAccount"
                    >
                      <el-option
                        v-for="item in accountNumberOptions"
                        :key="item.login"
                        :value="item.login"
                        :label="getCurrencySymbol(item)"
                        :data-testid="item.login"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="40">
                <el-col :xs="24" :sm="24">
                  <el-form-item prop="subStartDate">
                    <template #label>
                      <span>
                        {{ $t('promotion.vps.subStartDate') }}
                        <a :href="pdfDocument" target="_blank" class="pdf_link">{{
                          $t('promotion.vps.moreDetails')
                        }}</a>
                      </span>
                    </template>
                    <el-date-picker
                      v-model="form.subStartDate"
                      type="date"
                      format="dd-MM-yyyy"
                      value-format="yyyy-MM-dd"
                      :picker-options="{ disabledDate: disabledDate }"
                      placeholder="DD-MM-YYYY"
                    >
                    </el-date-picker>
                    <div v-if="claimExisted" class="el-form-item__error text-xs">
                      {{ $t('promotion.vps.claimExisted') }}
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="40">
                <el-col :xs="24" :sm="24">
                  <el-form-item :label="$t('promotion.vps.invNumber')" prop="invNumber">
                    <el-input
                      v-model="form.invNumber"
                      data-testid="invNumber"
                      :placeholder="$t('promotion.vps.invNumberPlaceholder')"
                    ></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
            </div>
            <div v-if="showInfoField">
              <el-form-item prop="agreeTnc" class="checkbox">
                <el-checkbox v-model="form.agreeTnc" data-testid="agreeTnc">
                  <i18n path="promotion.vps.agreeTnc">
                    <template v-slot:link>
                      <a :href="tncLink" target="_blank" data-testid="tnc" class="link_text">{{
                        $t('promotion.vps.tncLink')
                      }}</a>
                    </template>
                  </i18n></el-checkbox
                >
              </el-form-item>

              <el-form-item prop="acknowledgeTnc" class="checkbox">
                <el-checkbox
                  :label="$t('promotion.vps.acknowledgeTnc')"
                  v-model="form.acknowledgeTnc"
                  data-testid="acknowledgeTnc"
                >
                </el-checkbox>
              </el-form-item>
            </div>
          </el-form>
        </div>
        <div class="mt-4 footer" v-if="showInfoField">
          <el-button @click="cancelSubmit" data-testid="cancel">
            {{ $t('promotion.vps.cancel') }}
          </el-button>
          <el-button type="primary" @click="newVPSClick" data-testid="submit">
            {{ $t('promotion.vps.submit') }}
          </el-button>
        </div>
      </div>
    </vDialog>
  </div>
</template>

<script>
import vDialog from '@/components/home/vDialog';
import vUpload from '@/components/vUpload';
import { checkAgreedTNC } from '@/util/validation';
import splitThousands from '@/util/splitThousands';
import { apiGetVpsAccounts, apiVpsSubmit, apiVpsEligible, apiVpsGetTncDocuments } from '@/resource';
import moment from 'moment';
import isMobile from '@/mixins/isMobile';

export default {
  name: 'VpsDialog',
  props: {
    dialogVisible: Boolean,
    tncLink: String
  },
  components: { vDialog, vUpload },
  mixins: [isMobile],
  data() {
    return {
      form: {
        refundAccount: '',
        accountNumberOptions: [],
        invFile: [],
        subStartDate: null,
        invNumber: '',
        agreeTnc: false,
        acknowledgeTnc: false
      },
      visible: false,
      acceptableFiles: 'image/jpeg,image/jpg,image/png,application/pdf',
      allowedFiles: ['pdf', 'png', 'jpg', 'jpeg'],
      claimExisted: false,
      rules: {
        refundAccount: [
          {
            required: true,
            message: this.$t('common.formValidation.accReq'),
            trigger: 'blur'
          }
        ],
        subStartDate: [
          {
            required: true,
            message: this.$t('common.formValidation.dynamicReq', { dynamic: this.$t('promotion.vps.subStartDate') }),
            trigger: 'blur'
          }
        ],
        invNumber: [
          {
            required: true,
            message: this.$t('promotion.vps.invNumberReq'),
            trigger: 'blur'
          }
        ],
        agreeTnc: [
          {
            validator: checkAgreedTNC,
            message: this.$t('promotion.vps.tncReq'),
            trigger: 'blur'
          }
        ],
        acknowledgeTnc: [
          {
            validator: checkAgreedTNC,
            message: this.$t('promotion.vps.ackReq'),
            trigger: 'blur'
          }
        ]
      },
      disabledDate(date) {
        const threeMonthsAgo = moment().subtract(3, 'months').add(1, 'days');
        return moment(date).isBefore(threeMonthsAgo, 'day');
      },
      pdfDocument: ''
    };
  },
  watch: {
    dialogVisible(bool) {
      this.visible = bool;
    },
    visible(bool) {
      this.disableActivateVisible(bool);
    },
    showInfoField(bool) {
      if (!bool) this.$refs.form.resetFields();
    },
    'form.invNumber'(value) {
      this.form.invNumber = value.replace(/[^a-z0-9/]/gi, '');
    },
    'form.subStartDate'(value) {
      this.claimExisted = false;
    }
  },
  computed: {
    showInfoField() {
      return this.form.invFile.length > 0;
    }
  },
  methods: {
    cancelSubmit() {
      this.visible = false;
    },
    validateSubDate() {
      return apiVpsEligible({ selectedSubscriptionDate: this.form.subStartDate })
        .then(result => {
          if (result.data.code === 0) {
            if (result.data.data) {
              this.loading = false;
              this.claimExisted = true;
            } else {
              this.submitNewVPS();
            }
          }
        })
        .catch(err => {
          this.loading = false;
          this.$message({ message: this.$t('responseMsg.500'), type: 'error' });
        });
    },
    newVPSClick() {
      this.$refs['form'].validate(valid => {
        if (valid) {
          this.loading = true;
          this.validateSubDate();
        } else {
          return false;
        }
      });
    },
    submitNewVPS() {
      return apiVpsSubmit({
        accountNo: this.form.refundAccount,
        invoiceNo: this.form.invNumber,
        subscriptionDate: this.form.subStartDate,
        invoiceFilePath: this.form.invFile[0]
      })
        .then(result => {
          if (result.data.code === 0 && result.data.data === 'success') {
            this.$message({
              message: this.$t('promotion.vps.successMsg'),
              type: 'success'
            });
            this.cancelSubmit();
            this.$emit('updateHistoryTable');
          }
        })
        .catch(err => {
          this.loading = false;
          this.$message({ message: this.$t('responseMsg.500'), type: 'error' });
        });
    },
    updateFileInfo(fileInfo) {
      this.form.invFile = fileInfo.fileList;
    },
    disableActivateVisible(bool) {
      if (!bool) {
        this.$refs.upload.clearFile();
        this.$refs.form.resetFields();
      }
      this.$emit('update:dialogVisible', bool);
    },
    fetchTradingAccount() {
      apiGetVpsAccounts()
        .then(resp => {
          if (resp.data.code === 0) {
            this.accountNumberOptions = resp.data.data.logins;
          } else {
            this.$message({ message: this.$t('withdraw.default.fetchTradeAccFailed'), type: 'error' });
          }
        })
        .catch(err => {
          this.$message({ message: this.$t('withdraw.default.fetchTradeAccFailed'), type: 'error' });
        });
    },
    getCurrencySymbol(item) {
      return (
        item.login +
        ' - ' +
        this.$options.filters.currencyToSymbol(item.currency) +
        splitThousands(item.balance, 2) +
        ' ' +
        item.currency
      );
    },
    async getPdfDocument() {
      try {
        const { lang } = this.$store.state.common;
        const resp = await apiVpsGetTncDocuments();
        const doc =
          resp.data.data.find(i => i.languageCode === lang)?.document ||
          resp.data.data.find(i => i.languageCode === 'en_US').document;
        this.pdfDocument = window.location.origin + '/api' + doc;
      } catch (err) {
        console.error(err);
      }
    }
  },
  mounted() {
    this.fetchTradingAccount();
    this.getPdfDocument();
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/components/vps/vpsDialog.scss';
</style>

<style lang="scss">
.el-tooltip__popper.vps_tooltip {
  background-color: $primary-blue !important;
  border: $primary-blue !important;
  color: #fff !important;
  font-size: 12px;
  min-width: 280px;
  .popper__arrow,
  .popper__arrow::after {
    border-bottom-color: $primary-blue !important;
    border-top-color: $primary-blue !important;
  }
}
</style>
